import { RobotIcon } from '@/components/Icons'
import ActionHintBubble from '@/components/ActionHintBubble'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useAnimationReplay } from '@/hooks/useAnimationReplay'

type CopilotFloatingFabProps = {
  active: boolean
  unreadCount: number
  /** Bob a few times to be noticed, for a reader who has never opened the panel (BRAI-685). */
  nudge: boolean
  /** Say what the button is for, above it, for a few seconds (BRAI-685). */
  hint: boolean
  onClick: () => void
}

export default function CopilotFloatingFab({
  active,
  unreadCount,
  nudge,
  hint,
  onClick,
}: CopilotFloatingFabProps) {
  const { t } = useUiStrings()
  // Replayed on the unread count, so each suggestion that lands behind a shut
  // panel gets its own bob. Left as a steady class it would have played once,
  // on arrival, and never again however much came in after it.
  const bob = useAnimationReplay(nudge, unreadCount)

  return (
    // Positioned wrapper rather than a positioned button: the hint hangs off
    // this, and a `role="tooltip"` element is not valid inside a `<button>`.
    <div className="pointer-events-none fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-50">
      {/* Keyed on the count so a new suggestion remounts the bubble and starts
          its few seconds again. */}
      <ActionHintBubble
        key={unreadCount}
        id="copilot-action-hint-fab"
        text={unreadCount > 0 ? t.copilotNewItemsHint : t.copilotActionHint}
        show={hint}
      />
      <button
        type="button"
        onClick={onClick}
        data-testid="copilot-floating-fab"
        aria-describedby={hint ? 'copilot-action-hint-fab' : undefined}
        className={[
          'pointer-events-auto relative flex h-12 w-12 touch-manipulation items-center justify-center rounded-full transition-opacity',
          bob ? 'motion-safe:animate-nudge-bounce' : '',
          active ? 'opacity-90 ring-2 ring-blue-300' : 'opacity-100 hover:opacity-95',
        ].join(' ')}
        aria-pressed={active}
        title={t.copilotLabel}
        aria-label={t.copilotLabel}
      >
        <RobotIcon className="h-12 w-12" />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
            aria-label={`${unreadCount} unread`}
          >
            {unreadCount}
          </span>
        ) : null}
      </button>
    </div>
  )
}
